/**
 * What the end of a lesson should point to. Pure (catalog in, decision out),
 * so the funnel rule is unit tested and every lesson uses the same logic:
 *
 *   next_lesson  another lesson follows in this course: keep the momentum.
 *   next_course  last lesson of a course, the next course is free.
 *   pro_upsell   last lesson of the free path (Course 2): the next course is
 *                Pro, so this is the strongest moment to show what Pro is.
 *   finished     last lesson of the last course.
 */
import type { Course, Lesson } from '../content/catalog'

export type LessonNextStep =
  | { kind: 'next_lesson'; lesson: Lesson }
  | { kind: 'next_course'; course: Course; lesson: Lesson | null }
  | { kind: 'pro_upsell'; course: Course; lesson: Lesson | null }
  | { kind: 'finished' }

export function resolveLessonNextStep(input: {
  courses: ReadonlyArray<Course>
  lessons: ReadonlyArray<Lesson>
  courseSlug: string
  lessonSlug: string
}): LessonNextStep | null {
  const inCourse = input.lessons.filter((lesson) => lesson.courseSlug === input.courseSlug)
  const index = inCourse.findIndex((lesson) => lesson.slug === input.lessonSlug)
  if (index === -1) return null
  if (index + 1 < inCourse.length) {
    return { kind: 'next_lesson', lesson: inCourse[index + 1] }
  }

  const ordered = [...input.courses].sort((a, b) => a.order - b.order)
  const current = ordered.find((course) => course.slug === input.courseSlug)
  if (!current) return null
  const nextCourse = ordered.find((course) => course.order > current.order)
  if (!nextCourse) return { kind: 'finished' }
  const firstLesson =
    input.lessons.find((lesson) => lesson.courseSlug === nextCourse.slug) ?? null
  // Crossing from the free path into Pro is the upsell moment.
  if (nextCourse.tier === 'pro' && current.tier === 'free') {
    return { kind: 'pro_upsell', course: nextCourse, lesson: firstLesson }
  }
  return { kind: 'next_course', course: nextCourse, lesson: firstLesson }
}
